import { useCallback, useState } from "react";

import { getHighlyConfidentialValue } from "../api";

interface UseHighlyConfidentialValueParams {
  operationTaskInstanceId?: string;
  fieldId: string;
}

interface UseHighlyConfidentialValueReturn {
  /** 秘匿値が表示されているかどうか */
  isRevealed: boolean;
  /** 復号化された秘匿値 */
  secretValue: string | null;
  /** API呼び出し中かどうか */
  isLoading: boolean;
  /** 秘匿値の表示/非表示を切り替える */
  toggleReveal: () => Promise<void>;
}

/**
 * 秘匿値の表示/非表示を管理するカスタムフック
 */
export const useHighlyConfidentialValue = ({
  operationTaskInstanceId,
  fieldId,
}: UseHighlyConfidentialValueParams): UseHighlyConfidentialValueReturn => {
  const [isRevealed, setIsRevealed] = useState(false);
  const [secretValue, setSecretValue] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const toggleReveal = useCallback(async () => {
    if (isRevealed) {
      // 非表示にする
      setIsRevealed(false);
      setSecretValue(null);
      return;
    }

    // 表示する
    if (!operationTaskInstanceId) {
      console.warn("operationTaskInstanceId is not provided");
      return;
    }

    setIsLoading(true);
    try {
      const result = await getHighlyConfidentialValue(
        operationTaskInstanceId,
        fieldId,
      );
      setSecretValue(result.value);
      setIsRevealed(true);
    } catch (error) {
      console.error("Failed to fetch highly confidential value:", error);
      // エラー時は何もしない（ローディング状態を解除するのみ）
    } finally {
      setIsLoading(false);
    }
  }, [isRevealed, operationTaskInstanceId, fieldId]);

  return {
    isRevealed,
    secretValue,
    isLoading,
    toggleReveal,
  };
};
